/**
 * TenureModal — configurable modal for the full Tenure → Title → Experience hierarchy.
 *
 * Six modes determined by which props are supplied:
 *
 *  1. createNew         (no extra props)
 *     → Company + tenure dates + official title + title dates + optional project/team/role.
 *     → Creates: tenure → title → experience.
 *
 *  2. addTitle          (tenureId prop, no title/experience)
 *     → Shows title fields (title text + dates) only.
 *     → Creates a new title under the given tenure.
 *
 *  3. addExperience     (titleId prop, no title/experience)
 *     → Shows experience fields (project/team/role) only.
 *     → Creates a new experience under the given title.
 *
 *  4. editTenure        (tenure prop)
 *     → Company + date fields only.
 *
 *  5. editTitle         (title prop)
 *     → Title text + date fields only.
 *
 *  6. editExperience    (experience prop)
 *     → Project/team/role fields only.
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tenureApi, titleApi, experienceApi } from '@/lib/api';
import type { Tenure, Title, Experience } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// ── Form shape ─────────────────────────────────────────────────────────────────

interface FormValues {
  // Tenure fields
  company: string;
  tenureStartDate: string;
  tenureEndDate: string;
  // Title fields
  titleText: string;
  titleStartDate: string;
  titleEndDate: string;
  // Experience fields
  role: string;
  project: string;
  team: string;
}

const EMPTY: FormValues = {
  company: '',
  tenureStartDate: '',
  tenureEndDate: '',
  titleText: '',
  titleStartDate: '',
  titleEndDate: '',
  role: '',
  project: '',
  team: '',
};

// ── Props ──────────────────────────────────────────────────────────────────────

interface TenureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit existing tenure */
  tenure?: Tenure;
  /** Edit existing title */
  title?: Title;
  /** Edit existing experience */
  experience?: Experience;
  /** Add a new title to this tenure */
  tenureId?: string;
  tenureCompany?: string;
  /** Add a new experience to this title */
  titleId?: string;
  titleText?: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function TenureModal({
  open,
  onOpenChange,
  tenure,
  title,
  experience,
  tenureId,
  tenureCompany,
  titleId,
  titleText,
}: TenureModalProps) {
  const queryClient = useQueryClient();

  // Determine mode
  const isEditTenure     = !!tenure && !title && !experience;
  const isEditTitle      = !!title && !tenure && !experience;
  const isEditTenureAndTitle = !!tenure && !!title && !experience;
  const isEditExperience = !!experience;
  const isAddTitle       = !!tenureId && !experience && !title && !tenure;
  const isAddExperience  = !!titleId  && !experience && !title && !tenure;
  const isCreateNew      = !isEditTenure && !isEditTitle && !isEditExperience && !isAddTitle && !isAddExperience && !isEditTenureAndTitle;

  // Field visibility
  const showTenureFields  = isCreateNew || isEditTenure || isEditTenureAndTitle;
  const showTitleFields   = isCreateNew || isEditTitle || isAddTitle || isEditTenureAndTitle;
  const showExpFields     = isCreateNew || isEditExperience || isAddExperience;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    if ((isEditTenure || isEditTenureAndTitle) && tenure) {
      const values = { ...EMPTY, company: tenure.company, tenureStartDate: tenure.startDate ?? '', tenureEndDate: tenure.endDate ?? '' };
      if (isEditTenureAndTitle && title) {
        values.titleText = title.title;
        values.titleStartDate = title.startDate ?? '';
        values.titleEndDate = title.endDate ?? '';
      }
      reset(values);
    } else if (isEditTitle && title) {
      reset({ ...EMPTY, titleText: title.title, titleStartDate: title.startDate ?? '', titleEndDate: title.endDate ?? '' });
    } else if (isEditExperience && experience) {
      reset({ ...EMPTY, role: experience.role ?? '', project: experience.project ?? '', team: experience.team ?? '' });
    } else {
      reset(EMPTY);
    }
  }, [open, tenure, title, experience, isEditTenure, isEditTitle, isEditTenureAndTitle, isEditExperience, reset]);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenures'] });
    void queryClient.invalidateQueries({ queryKey: ['titles'] });
    void queryClient.invalidateQueries({ queryKey: ['experiences'] });
  };

  const createTenureMut   = useMutation({ mutationFn: tenureApi.create });
  const updateTenureMut   = useMutation({
    mutationFn: (data: Parameters<typeof tenureApi.update>[1]) => tenureApi.update(tenure!.id, data),
  });
  const createTitleMut    = useMutation({ mutationFn: titleApi.create });
  const updateTitleMut    = useMutation({
    mutationFn: (data: Parameters<typeof titleApi.update>[1]) => titleApi.update(title!.id, data),
  });
  const createExpMut      = useMutation({ mutationFn: experienceApi.create });
  const updateExpMut      = useMutation({
    mutationFn: (data: Parameters<typeof experienceApi.update>[1]) => experienceApi.update(experience!.id, data),
  });

  const isPending =
    createTenureMut.isPending || updateTenureMut.isPending ||
    createTitleMut.isPending  || updateTitleMut.isPending  ||
    createExpMut.isPending    || updateExpMut.isPending;

  // ── Submit ──────────────────────────────────────────────────────────────────

  const onSubmit = async (values: FormValues) => {
    try {
      // Mode 4: edit tenure (and optionally title)
      if (isEditTenure || isEditTenureAndTitle) {
        await updateTenureMut.mutateAsync({
          company: values.company.trim(),
          startDate: values.tenureStartDate.trim() || undefined,
          endDate: values.tenureEndDate.trim() || undefined,
        });
        if (isEditTenureAndTitle && title) {
          await updateTitleMut.mutateAsync({
            title: values.titleText.trim(),
            startDate: values.titleStartDate.trim() || undefined,
            endDate: values.titleEndDate.trim() || undefined,
          });
        }
        invalidate(); onOpenChange(false); return;
      }

      // Mode 5: edit title
      if (isEditTitle) {
        await updateTitleMut.mutateAsync({
          title: values.titleText.trim(),
          startDate: values.titleStartDate.trim() || undefined,
          endDate: values.titleEndDate.trim() || undefined,
        });
        invalidate(); onOpenChange(false); return;
      }

      // Mode 6: edit experience
      if (isEditExperience) {
        await updateExpMut.mutateAsync({
          role: values.role.trim() || null,
          project: values.project.trim() || null,
          team: values.team.trim() || null,
        });
        invalidate(); onOpenChange(false); return;
      }

      // Mode 2: add title to existing tenure
      if (isAddTitle) {
        await createTitleMut.mutateAsync({
          tenureId: tenureId!,
          title: values.titleText.trim() || 'Professional',
          startDate: values.titleStartDate.trim() || undefined,
          endDate: values.titleEndDate.trim() || undefined,
        });
        invalidate(); onOpenChange(false); return;
      }

      // Mode 3: add experience to existing title
      if (isAddExperience) {
        await createExpMut.mutateAsync({
          titleId: titleId!,
          role: values.role.trim() || undefined,
          project: values.project.trim() || undefined,
          team: values.team.trim() || undefined,
        });
        invalidate(); onOpenChange(false); return;
      }

      // Mode 1: create new (tenure + title + experience)
      const newTenure = await createTenureMut.mutateAsync({
        company: values.company.trim(),
        startDate: values.tenureStartDate.trim() || undefined,
        endDate: values.tenureEndDate.trim() || undefined,
      });

      const newTitle = await createTitleMut.mutateAsync({
        tenureId: newTenure.id,
        title: values.titleText.trim() || 'Professional',
        startDate: values.titleStartDate.trim() || undefined,
        endDate: values.titleEndDate.trim() || undefined,
      });

      // Always create a first experience so the user can add bullets immediately
      await createExpMut.mutateAsync({
        titleId: newTitle.id,
        role: values.role.trim() || undefined,
        project: values.project.trim() || undefined,
        team: values.team.trim() || undefined,
      });

      invalidate(); onOpenChange(false);
    } catch (err) {
      setError('root', { message: err instanceof Error ? err.message : 'Something went wrong' });
    }
  };

  // ── Dialog title ────────────────────────────────────────────────────────────

  let dialogTitle = 'Add experience';
  if (isEditTenureAndTitle) dialogTitle = 'Edit company and title';
  else if (isEditTenure)      dialogTitle = 'Edit company';
  else if (isEditTitle)  dialogTitle = 'Edit title';
  else if (isEditExperience) dialogTitle = 'Edit project / team';
  else if (isAddTitle)   dialogTitle = tenureCompany ? `Add title at ${tenureCompany}` : 'Add title';
  else if (isAddExperience) dialogTitle = titleText ? `Add project under "${titleText}"` : 'Add project / team';

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.root && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {errors.root.message}
            </div>
          )}

          {/* ── Tenure fields ─────────────────────────────────────── */}
          {showTenureFields && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="company">
                  Company <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="company"
                  placeholder="e.g. Acme Corp"
                  {...register('company', { required: isCreateNew ? 'Company is required' : false })}
                />
                {errors.company && (
                  <p className="text-xs text-destructive">{errors.company.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tenureStartDate">Employment start</Label>
                  <Input id="tenureStartDate" type="date" {...register('tenureStartDate')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tenureEndDate">Employment end</Label>
                  <Input id="tenureEndDate" type="date" {...register('tenureEndDate')} />
                  <p className="text-xs text-muted-foreground">Leave blank if current</p>
                </div>
              </div>
            </>
          )}

          {/* ── Title fields ──────────────────────────────────────── */}
          {showTitleFields && (
            <>
              {showTenureFields && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Official title
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="titleText">
                  Job title {isCreateNew && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="titleText"
                  placeholder="e.g. Senior Software Engineer"
                  {...register('titleText', { required: (isCreateNew || isAddTitle) ? 'Job title is required' : false })}
                />
                {errors.titleText && (
                  <p className="text-xs text-destructive">{errors.titleText.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="titleStartDate">Title start</Label>
                  <Input id="titleStartDate" type="date" {...register('titleStartDate')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="titleEndDate">Title end</Label>
                  <Input id="titleEndDate" type="date" {...register('titleEndDate')} />
                  <p className="text-xs text-muted-foreground">Leave blank if current</p>
                </div>
              </div>
            </>
          )}

          {/* ── Experience fields ─────────────────────────────────── */}
          {showExpFields && (
            <>
              {(showTenureFields || showTitleFields) && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Project / assignment {isCreateNew && <span className="font-normal normal-case">(optional)</span>}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="project">Project</Label>
                  <Input id="project" placeholder="e.g. Payments v2" {...register('project')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="team">Team</Label>
                  <Input id="team" placeholder="e.g. Platform" {...register('team')} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="role">Functional role</Label>
                <Input
                  id="role"
                  placeholder="e.g. Scrum Master, Product Owner (optional)"
                  {...register('role')}
                />
                <p className="text-xs text-muted-foreground">
                  The role you played on this project, distinct from your official job title.
                </p>
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? 'Saving…'
                : isEditTenure || isEditTitle || isEditExperience
                  ? 'Save changes'
                  : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
